import React from 'react';
import { usePopper } from 'react-popper';

interface ProjectContribution {
  id: string;
  name: string;
  monthlyBudget: number;
  months: number;
  totalContribution: number;
}

interface CustomerYearlyBudget {
  customerName: string;
  color?: string;
  totalBudget: number;
  projects: { [key: string]: ProjectContribution[] };
  [key: string]: any;
}

interface YearlyBudgetTableProps {
  customerBudgets: CustomerYearlyBudget[];
  years: string[];
  formatBudget: (value: number) => string;
}

export function YearlyBudgetTable({ customerBudgets, years, formatBudget }: YearlyBudgetTableProps) {
  const [tooltipContent, setTooltipContent] = React.useState<ProjectContribution[] | null>(null);
  const [referenceElement, setReferenceElement] = React.useState<HTMLElement | null>(null);
  const [popperElement, setPopperElement] = React.useState<HTMLElement | null>(null);
  const [arrowElement, setArrowElement] = React.useState<HTMLElement | null>(null);

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'top',
    modifiers: [
      { name: 'arrow', options: { element: arrowElement } },
      { name: 'offset', options: { offset: [0, 8] } },
      { name: 'preventOverflow', options: { padding: 8 } }
    ],
  });

  const calculateYearlyTotals = () => {
    const yearlyTotals: { [key: string]: number } = {};
    years.forEach(year => {
      yearlyTotals[year] = customerBudgets.reduce((sum, customer) => sum + (customer[year] || 0), 0);
    });
    return yearlyTotals;
  };

  const handleCellHover = (
    e: React.MouseEvent<HTMLTableCellElement>,
    projects: ProjectContribution[]
  ) => {
    if (projects.length === 0) {
      setTooltipContent(null);
      setReferenceElement(null);
      return;
    }

    setReferenceElement(e.currentTarget);
    setTooltipContent(projects.sort((a, b) => b.totalContribution - a.totalContribution));
  };

  const handleCellLeave = () => {
    setTooltipContent(null);
    setReferenceElement(null);
  };

  const yearlyTotals = calculateYearlyTotals();
  const totalBudget = customerBudgets.reduce((sum, item) => sum + item.totalBudget, 0);

  return (
    <>
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Budget Breakdown by Year</h2>
          <p className="text-gray-600">
            Detailed breakdown of project budgets by customer and year
          </p>
        </div>

        <div className="relative overflow-x-auto" style={{ maxHeight: 'calc(100vh - 300px)' }}>
          <table className="min-w-full divide-y divide-gray-200" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead className="sticky top-0 z-20 bg-white">
              <tr>
                <th className="sticky left-0 z-30 bg-white px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                  Customer
                </th>
                {years.map(year => (
                  <th key={year} className="sticky top-0 z-20 bg-white px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                    {year}
                  </th>
                ))}
                <th className="sticky top-0 z-20 bg-white px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                  Total
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {customerBudgets.map((item) => (
                <tr key={item.customerName}>
                  <td className="sticky left-0 z-10 bg-white px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {item.customerName}
                  </td>
                  {years.map(year => (
                    <td
                      key={year}
                      className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 cursor-help hover:bg-gray-50"
                      onMouseEnter={(e) => handleCellHover(e, item.projects[year])}
                      onMouseLeave={handleCellLeave}
                    >
                      {item[year] ? formatBudget(item[year]) : '—'}
                    </td>
                  ))}
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                    {formatBudget(item.totalBudget)}
                  </td>
                </tr>
              ))}
              <tr className="bg-gray-50 font-semibold">
                <td className="sticky left-0 z-10 bg-gray-50 px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  Total
                </td>
                {years.map(year => (
                  <td key={year} className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                    {yearlyTotals[year] ? formatBudget(yearlyTotals[year]) : '—'}
                  </td>
                ))}
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                  {formatBudget(totalBudget)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {tooltipContent && (
        <div
          ref={setPopperElement}
          style={styles.popper}
          {...attributes.popper}
          className="z-50 bg-white rounded-lg shadow-lg border border-gray-200 p-4 max-w-md"
        >
          <div ref={setArrowElement} style={styles.arrow} />
          <h4 className="font-medium text-gray-900 mb-2">Projects Contributing to Total</h4>
          <div className="space-y-3">
            {tooltipContent.map((project) => (
              <div key={project.id} className="border-b border-gray-100 last:border-0 pb-2 last:pb-0">
                <div className="font-medium text-gray-800">{project.name}</div>
                <div className="text-sm text-gray-600 mt-1">
                  <div>Monthly: {formatBudget(project.monthlyBudget)}</div>
                  <div>Months: {project.months}</div>
                  <div className="font-medium text-indigo-600">
                    Total: {formatBudget(project.totalContribution)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}